import { useEffect, useState } from 'react'
import { Check, Copy, Loader2, Share2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { apiFetch } from '@/lib/apiFetch'
import { capture } from '@/lib/analytics'

interface ShareLinkResponse {
  token: string
  url: string
  expires_at: string
}

interface ShareDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  draftId: string | null
  /** Title is for the dialog header only; the snapshot itself is
   *  assembled server-side from the draft + audit. */
  draftTitle: string
}

/**
 * Minimal v1 Share dialog per docs/general_roadmap.md (18/05/2026):
 * single "Copy URL" button + a one-line expiry note. No selector, no
 * revoke button — the 7-day expiry covers the worst case, the DELETE
 * endpoint stays available for admin override.
 *
 * Opens, POSTs to /api/drafts/{id}/share immediately (so we have a
 * link to show), then surfaces the URL.
 */
export function ShareDialog({ open, onOpenChange, draftId, draftTitle }: ShareDialogProps) {
  const [link, setLink] = useState<ShareLinkResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  // Fire the create call whenever the dialog transitions to open.
  // Radix only invokes its onOpenChange for user-driven dismissals
  // (escape / outside-click / close button) — not for the parent
  // flipping `open` to true via the Share button. An effect on `open`
  // is the right hook for that side.
  useEffect(() => {
    if (!open) {
      setLink(null)
      setError(null)
      setCopied(false)
      setLoading(false)
      return
    }
    if (!draftId) {
      setError('Save the draft before sharing.')
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    setLink(null)
    setCopied(false)
    apiFetch(`/api/drafts/${draftId}/share`, { method: 'POST' })
      .then(async (res) => {
        if (cancelled) return
        if (!res.ok) {
          let detail: string = res.statusText
          try {
            const body = await res.json()
            detail = body?.detail ?? detail
          } catch { /* not JSON */ }
          // 422 = no completed audit yet — keep the message specific.
          throw new Error(detail)
        }
        const body: ShareLinkResponse = await res.json()
        if (!cancelled) {
          setLink(body)
          capture('share_link_created')
        }
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to create share link')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [open, draftId])

  async function handleCopy() {
    if (!link) return
    try {
      await navigator.clipboard.writeText(link.url)
      setCopied(true)
      toast.success('Link copied to clipboard')
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Copy failed — select the URL manually')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Share2 className="h-4 w-4" strokeWidth={1.5} />
            Share "{draftTitle || 'Untitled'}"
          </DialogTitle>
          <DialogDescription>
            Anyone with the link can view a read-only copy — the draft text,
            citations, and verified passages.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-3">
              <Loader2 className="h-4 w-4 animate-spin" />
              Creating share link…
            </div>
          )}

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {error}
            </p>
          )}

          {link && !loading && (
            <div className="flex flex-col gap-2">
              <div className="flex items-stretch gap-2">
                <input
                  type="text"
                  readOnly
                  value={link.url}
                  onFocus={(e) => e.currentTarget.select()}
                  className="flex-1 min-w-0 rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <Button
                  size="sm"
                  onClick={handleCopy}
                  className="bg-pharma-primary hover:bg-pharma-primary/90 text-white shrink-0 gap-2"
                >
                  {copied
                    ? <><Check className="h-4 w-4" /> Copied</>
                    : <><Copy className="h-4 w-4" /> Copy URL</>}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Link expires in 7 days.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="mt-2">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
